/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';
import classnames from 'classnames';
import { debounce, isEmpty, noop } from 'lodash';

import Input from 'components/inputs/input/Input';
import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import InfluencerVerificationLabel from 'components/influencer-verification-label/InfluencerVerificationLabel';
import Popover from 'components/popover/Popover';
import ActionLink from 'components/buttons/action-link/ActionLink';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import DiscoveryCreatorsFiltersGeneralHeader from './header/DiscoveryCreatorsFiltersGeneralHeader';
import DiscoveryFiltersTitle from 'modules/discovery/components/filters/title/DiscoveryFiltersTitle';
import DiscoveryCreatorsFilterPopover from 'modules/freemium/components/discovery-creators-filter-popover/DiscoveryCreatorsFilterPopover';

import { DEBOUNCE_DELAY } from 'modules/discovery/creators/utils/creators';
import { __t, applyTranslations } from 'utils/language';
import {
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE
} from 'utils/channels';

import './discovery-creators-filters-general.scss';

import type {
  DiscoveryCreatorsFilter,
  DiscoveryCreatorsFilters,
  DiscoveryFilterCheckbox,
  DiscoveryVerificationLabel,
  TextEntry
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./discovery-creators-filters-general.i18n.json'));
applyTranslations(require('components/influencer-verification-label/influencer-verification-label.i18n.json'));

type Props = {
  filters: DiscoveryCreatorsFilters,
  isFreemium: boolean,
  handleDiscoveryFiltersUpdate: (a: DiscoveryCreatorsFilter) => void,
  handleDiscoveryFiltersReset: () => void
};

type State = {
  searchByName: string
};

const FREEMIUM_CHECKBOXES = [
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE
];

const generalMsgKey = 'discovery_creators_filters_general';
const verificationMsgKey = 'influencer_verification_label';

class DiscoveryCreatorsFiltersGeneral extends React.PureComponent<Props, State> {
  static defaultProps = {
    filters: {},
    handleDiscoveryFiltersUpdate: () => {},
    handleDiscoveryFiltersReset: () => {}
  };

  debounceMethod = noop;

  constructor(props: Props) {
    super(props);

    this.debounceMethod = debounce(this.handleUpdate, DEBOUNCE_DELAY);

    this.state = {
      searchByName: props.filters.searchByName || ''
    };
  }

  componentDidMount() {
    const { filters, isFreemium } = this.props;

    // when freemium, check both profile boxes by default (COLLAB-12525)
    if (isFreemium) {
      const updatedProfileCheckboxes = filters.profiles.map(item => ({
        ...item,
        selected: true
      }));

      this.handleUpdate({
        profiles: updatedProfileCheckboxes
      });
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { filters } = this.props;

    if (filters.searchByName !== prevProps.filters.searchByName) {
      this.setState({
        searchByName: filters.searchByName
      });
    }
  }

  getCheckboxClasses(type: 'vertical' | 'horizontal'): string {
    return classnames('discovery-creators-filters-general__section-checkboxes', {
      [`discovery-creators-filters-general__section-checkboxes--layout-${type}`]: Boolean(type)
    });
  }

  handleResetFilters = (): void => {
    const { handleDiscoveryFiltersReset } = this.props;

    handleDiscoveryFiltersReset();
  };

  handleNameChange = (event: SyntheticEvent<TextEntry>): void => {
    const target = event.currentTarget;
    const name = target.name;
    const value = target.value || '';

    this.setState({
      searchByName: value
    }, () => {
      const result = {
        [name]: value
      };

      if (this.debounceMethod) {
        this.debounceMethod(result);
      }
    });
  };

  handleClearSearchInput = () => {
    const result = {
      searchByName: ''
    };

    this.setState(result, () => this.handleUpdate(result));
  };

  handleCheckboxChange = (event: SyntheticEvent<HTMLInputElement>): void => {
    const { filters } = this.props;
    const target = event.currentTarget;
    const name = target.name;
    const parent = target.dataset.parent || '';
    const identifier = target.dataset.identifier || '';
    const isChecked = target.checked;

    if (parent && identifier && name) {
      const arr = filters[parent];
      const selectedItem = arr.find(item => item.identifier === identifier);

      const updatedFilters = arr.map(item => {
        if (item.identifier === selectedItem.identifier) {
          return {
            ...selectedItem,
            value: String(isChecked),
            selected: isChecked
          };
        }

        return item;
      });

      this.handleUpdate({
        [parent]: updatedFilters
      });
    }
  };

  handleUpdate = (result: DiscoveryCreatorsFilter): void => {
    const { handleDiscoveryFiltersUpdate } = this.props;

    if (handleDiscoveryFiltersUpdate) {
      handleDiscoveryFiltersUpdate(result);
    }
  };

  renderChannelsPopover(children: React.ReactNode): React.ReactNode {
    return (
      <DiscoveryCreatorsFilterPopover
        triggerComponent={children}
      />
    );
  }

  renderCheckbox(values: DiscoveryFilterCheckbox, isDisabled: boolean): React.ReactNode {
    const identifier = values.identifier;

    return (
      <InputCheckbox
        key={`discovery-creators-filters-general__checkbox-${identifier}`}
        id={`discovery-creators-filters-general__checkbox-${identifier}`}
        name={values.name as string}
        label={values.label}
        value={String(values.value)}
        checked={values.checked}
        disabled={isDisabled}
        data={{
          identifier,
          parent: values.parent
        }}
        onChange={this.handleCheckboxChange}
      />
    );
  }

  renderVerificationLabel(type: DiscoveryVerificationLabel): React.ReactNode {
    return (
      <div className="discovery-creators-filters-general__verification-label">
        <InfluencerVerificationLabel
          type={type}
        />
      </div>
    );
  }

  renderProfilesPopover = (): JSX.Element => (
    <div className="discovery-creators-filters-general__popover-icon">
      <Popover showQuestionMark>
        <div className="discovery-creators-filters-general__popover">
          {__t(`${verificationMsgKey}.all`)}&nbsp;
          <ActionLink
            openInNewTab
            size="inherit"
            color="secondary"
            decoration="none"
            link="https://help.collabary.com/en/articles/3493347-content-creator-profiles"
          >
            {__t(`${verificationMsgKey}.link`)}
          </ActionLink>
        </div>
      </Popover>
    </div>
  );

  renderNameField(): React.ReactNode {
    const { searchByName } = this.state;

    return (
      <div className="discovery-creators-filters-general__section">
        <DiscoveryFiltersTitle
          title={__t(`${generalMsgKey}.labels.search_by_name`)}
        />
        <div className="discovery-creators-filters-general__search-by-name-wrapper">
          <Input
            name="searchByName"
            value={searchByName}
            autoComplete="off"
            onChange={this.handleNameChange}
          />
          <div className="discovery-creators-filters-general__search-by-name-cross">
            {searchByName && (
              <SVGDynamicIcon
                name="cross-custom-fg"
                width={12}
                height={12}
                customColor="grey"
                clickable
                onClick={this.handleClearSearchInput}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  renderProfileCheckboxes(): React.ReactNode {
    const { filters } = this.props;
    const profiles = filters.profiles;

    const overlayClasses = classnames('discovery-creators-filters-general__section-overlay', {
      'discovery-creators-filters-general__section-overlay--is-visible': !isEmpty(filters.searchByName)
    });

    return (
      <div className="discovery-creators-filters-general__section">
        <DiscoveryFiltersTitle
          title={__t(`${generalMsgKey}.labels.display_profiles`)}
        >
          {this.renderProfilesPopover()}
        </DiscoveryFiltersTitle>
        <div className={this.getCheckboxClasses('vertical')}>
          {
            (profiles || []).map(profile => {
              const checkboxProps = {
                identifier: profile.identifier,
                name: profile.identifier,
                label: profile.label,
                value: profile.selected,
                checked: profile.selected,
                parent: 'profiles'
              };

              return (
                <div
                  key={`discovery-creators-filters-general__${profile.identifier}`}
                  className="discovery-creators-filters-general__section-checkbox"
                >
                  {
                    this.renderCheckbox(checkboxProps, false)
                  }
                  {this.renderVerificationLabel(profile.identifier as DiscoveryVerificationLabel)}
                </div>
              );
            })
          }
        </div>
        <div className={overlayClasses}>
          {__t(`${generalMsgKey}.messages.search_by_name`)}
        </div>
      </div>
    );
  }

  renderChannels(): React.ReactNode {
    const { filters, isFreemium } = this.props;
    const channels = filters.channels;

    return (
      <div className="discovery-creators-filters-general__section">
        <DiscoveryFiltersTitle
          title={__t(`${generalMsgKey}.labels.channel`)}
        />
        <div className={this.getCheckboxClasses('horizontal')}>
          {
            (channels || []).map(channel => {
              const checkboxProps = {
                identifier: channel.identifier,
                name: channel.value,
                label: channel.label,
                value: channel.selected,
                checked: channel.selected,
                parent: 'channels'
              };

              const isDisabled = isFreemium && !FREEMIUM_CHECKBOXES.includes(channel.identifier);

              return (
                <div
                  key={`discovery-creators-filters-general__${channel.identifier}`}
                  className="discovery-creators-filters-general__section-checkbox"
                >
                  {
                    isDisabled
                      ? this.renderChannelsPopover(this.renderCheckbox(checkboxProps, isDisabled))
                      : this.renderCheckbox(checkboxProps, isDisabled)
                  }
                </div>
              );
            })
          }
        </div>
      </div>
    );
  }

  renderHeader(): React.ReactNode {
    return (
      <div className="discovery-creators-filters-general__header">
        <DiscoveryCreatorsFiltersGeneralHeader
          onResetFilters={this.handleResetFilters}
        />
      </div>
    );
  }

  renderContent(): React.ReactNode {
    return (
      <div className="discovery-creators-filters-general__content">
        {this.renderNameField()}
        {this.renderProfileCheckboxes()}
        {this.renderChannels()}
      </div>
    );
  }

  render(): React.ReactNode {
    return (
      <div className="discovery-creators-filters-general">
        {this.renderHeader()}
        {this.renderContent()}
      </div>
    );
  }
}

export default DiscoveryCreatorsFiltersGeneral;
